import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Send, PartyPopper, ClipboardList, Megaphone } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import api, { formatApiError } from "../lib/api";

const SurveySection = () => {
  const [survey, setSurvey] = useState(undefined);
  const [missing, setMissing] = useState(false);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    api
      .get("/surveys")
      .then((r) => setSurvey(r.data[0] || null))
      .catch(() => setLoadError("The survey couldn't load — try a refresh."));
  }, []);

  const choiceQuestions = survey ? survey.questions.filter((q) => q.type === "choice") : [];
  const answeredCount = choiceQuestions.filter((q) => answers[q.id]).length;
  const progress = choiceQuestions.length ? (answeredCount / choiceQuestions.length) * 100 : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!survey) return;
    const unanswered = choiceQuestions.filter((q) => !answers[q.id]);
    if (unanswered.length > 0) {
      setMissing(true);
      document.getElementById(`q-${unanswered[0].id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setMissing(false);
    setSubmitting(true);
    try {
      await api.post(`/surveys/${survey.id}/respond`, {
        answers: survey.questions
          .filter((q) => answers[q.id])
          .map((q) => ({ question_id: q.id, value: answers[q.id] })),
      });
      setDone(true);
    } catch (err) {
      alert(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="survey" className="relative py-16 md:py-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal>
          <Eyebrow>The survey</Eyebrow>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
            {survey?.title || "The Sidelines Survey"}
          </h2>
          <p className="mt-3 text-muted-foreground">
            {survey?.intro || "Around 3 minutes, anonymous, and no wrong answers."}
          </p>
        </Reveal>

        {loadError && <p className="mt-6 text-sm text-flame font-medium" data-testid="survey-error">{loadError}</p>}

        {survey === undefined && !loadError && (
          <div className="mt-10 grid place-items-center py-16 text-muted-foreground" data-testid="survey-loading">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}

        {survey === null && !loadError && (
          <div className="mt-10 rounded-3xl border-2 border-dashed border-foreground/15 p-10 text-center" data-testid="survey-paused">
            <Megaphone className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="mt-3 font-display font-bold text-lg">The survey is on a half-time break</p>
            <p className="text-sm text-muted-foreground mt-1">
              It'll be back shortly — vote in the poll or read the voices in the meantime.
            </p>
          </div>
        )}

        {survey && (
          <Reveal delay={0.1}>
            <div className="mt-8 rounded-3xl bg-card border-2 border-foreground/10 p-6 md:p-10 shadow-sm">
              {done ? (
                <div data-testid="survey-success-state" className="text-center py-8">
                  <motion.div
                    initial={{ scale: 0, rotate: -30 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 260, damping: 16 }}
                    className="mx-auto h-20 w-20 rounded-full bg-turf/20 grid place-items-center"
                  >
                    <PartyPopper className="h-9 w-9 text-pitch dark:text-turf" />
                  </motion.div>
                  <h3 className="mt-6 font-display text-2xl sm:text-3xl font-bold tracking-tight">Thank you — properly.</h3>
                  <p className="mt-3 text-muted-foreground max-w-md mx-auto">
                    Your answers just became part of the research. Share this site with your mates —
                    the more of us answer, the louder the findings get.
                  </p>
                  <button
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    className="mt-8 h-12 px-7 rounded-full bg-pitch text-cream font-semibold hover:bg-pitch-deep transition-colors dark:bg-turf dark:text-background"
                  >
                    Back to the top
                  </button>
                </div>
              ) : (
                <form data-testid="survey-form" onSubmit={submit} className="space-y-8">
                  <div>
                    <div className="flex justify-between font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-2">
                      <span>{answeredCount} of {choiceQuestions.length} answered</span>
                      <span>{Math.round(progress)}%</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-muted overflow-hidden">
                      <motion.div
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                        className="h-full rounded-full bg-turf"
                      />
                    </div>
                  </div>

                  {survey.questions.map((q, qi) => (
                    <div key={q.id} id={`q-${q.id}`} className="scroll-mt-28">
                      <p className="font-semibold leading-snug">
                        <span className="font-mono text-xs text-flame mr-2">{String(qi + 1).padStart(2, "0")}</span>
                        {q.text}
                        {q.type === "choice" && <span className="text-muted-foreground font-normal"> *</span>}
                      </p>
                      {q.type === "choice" ? (
                        <div className={`mt-3 flex flex-wrap gap-2 rounded-2xl ${missing && !answers[q.id] ? "ring-2 ring-flame rounded-2xl p-2 -m-2" : ""}`}>
                          {q.options.map((opt) => {
                            const active = answers[q.id] === opt;
                            return (
                              <button
                                type="button"
                                key={opt}
                                data-testid="survey-choice-option"
                                onClick={() => setAnswers((a) => ({ ...a, [q.id]: opt }))}
                                className={`px-4 py-2.5 rounded-full border-2 text-sm font-medium transition-all ${
                                  active
                                    ? "border-transparent bg-pitch text-cream dark:bg-turf dark:text-background"
                                    : "border-foreground/10 bg-background hover:border-pitch/40 dark:hover:border-turf/50"
                                }`}
                              >
                                {opt}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <textarea
                          data-testid="survey-text-input"
                          value={answers[q.id] || ""}
                          onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                          rows={3}
                          placeholder="Write as much or as little as you like…"
                          className="mt-3 w-full rounded-2xl border-2 border-foreground/10 bg-background px-4 py-3 text-sm sm:text-base focus:outline-none focus:border-pitch dark:focus:border-turf resize-y"
                        />
                      )}
                    </div>
                  ))}

                  {missing && (
                    <p data-testid="survey-missing-warning" className="text-sm text-flame font-medium">
                      Almost there — just answer the highlighted question(s) above.
                    </p>
                  )}

                  <div className="flex flex-wrap items-center gap-4 pt-2">
                    <button
                      type="submit"
                      data-testid="survey-submit-btn"
                      disabled={submitting}
                      className="inline-flex items-center gap-2 h-12 px-8 rounded-full bg-pitch text-cream font-semibold hover:bg-pitch-deep disabled:opacity-50 transition-colors dark:bg-turf dark:text-background"
                    >
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      Send my answers
                    </button>
                    <p className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                      <ClipboardList className="h-4 w-4" /> Anonymous — I never see who said what.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};

export default SurveySection;
